import React, { useEffect, useState, useCallback } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ban } from "lucide-react";

export default function BrandCancellationReasons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(100);
  const [isNoShow, setIsNoShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.get("/cancellation-reasons");
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setLabel("");
    setActive(true);
    setSortOrder(100);
    setIsNoShow(false);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setLabel(r.label);
    setActive(r.active);
    setSortOrder(r.sort_order ?? 100);
    setIsNoShow(!!r.is_no_show);
    setOpen(true);
  };

  const save = async () => {
    const v = label.trim();
    if (!v) return toast.error("Label is required.");
    setBusy(true);
    try {
      if (editing) {
        await staffApi.put(`/cancellation-reasons/${editing.id}`, {
          label: v,
          active,
          sort_order: Number(sortOrder) || 100,
          is_no_show: isNoShow,
        });
        toast.success("Reason updated.");
      } else {
        await staffApi.post("/cancellation-reasons", {
          label: v,
          active,
          sort_order: Number(sortOrder) || 100,
          is_no_show: isNoShow,
        });
        toast.success("Reason created.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await staffApi.put(`/cancellation-reasons/${r.id}`, { active: !r.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.label}"? This cannot be undone.`)) return;
    try {
      await staffApi.delete(`/cancellation-reasons/${r.id}`);
      toast.success("Reason deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <StaffShell context="brand">
      <div className="max-w-4xl">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <p className="tracking-overline text-[11px] text-muted-foreground">GENERAL</p>
            <h1 className="font-editorial text-4xl mt-2">Cancellation Reasons</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              These reasons are shown to customers when they cancel or reschedule a
              future visit. Deactivate a reason to hide it from customers without
              losing its history.
            </p>
          </div>
          <Button onClick={openNew} data-testid="add-reason-btn" className="rounded-full gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Add reason
          </Button>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                <Ban className="w-6 h-6 opacity-60" />
                <p>No cancellation reasons yet. Add one to get started.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-4 px-5 py-4"
                    data-testid={`reason-row-${r.id}`}
                  >
                    <div className="w-12 text-xs text-muted-foreground tabular-nums">#{r.sort_order}</div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${r.active ? "text-foreground" : "text-muted-foreground line-through"}`}>
                        {r.label}
                        {r.is_no_show && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
                            data-testid={`reason-noshow-badge-${r.id}`}
                          >
                            No-show
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={r.active}
                        onCheckedChange={() => toggleActive(r)}
                        data-testid={`reason-toggle-${r.id}`}
                        aria-label={r.active ? "Deactivate" : "Activate"}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(r)}
                        data-testid={`reason-edit-${r.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(r)}
                        data-testid={`reason-delete-${r.id}`}
                        aria-label="Delete"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit reason" : "New cancellation reason"}</DialogTitle>
            <DialogDescription>
              Customers will see active reasons when they cancel a future booking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="reason-label">Label</Label>
              <Input
                id="reason-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Feeling unwell"
                className="mt-1"
                data-testid="reason-label-input"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reason-order">Sort order</Label>
                <Input
                  id="reason-order"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="mt-1"
                  data-testid="reason-order-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Lower = shown first.</p>
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-2 flex items-center gap-2 h-9">
                  <Switch checked={active} onCheckedChange={setActive} data-testid="reason-active-switch" />
                  <span className="text-sm text-muted-foreground">{active ? "Active" : "Hidden"}</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label htmlFor="reason-noshow-switch" className="text-sm font-medium">No-show reason</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Appointments cancelled with this reason stay on the calendar
                    (marked grey) instead of being removed. Use it for &ldquo;No Show&rdquo;.
                  </p>
                </div>
                <Switch
                  id="reason-noshow-switch"
                  checked={isNoShow}
                  onCheckedChange={setIsNoShow}
                  data-testid="reason-noshow-switch"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="reason-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="reason-save-btn">
              {busy ? "Saving…" : editing ? "Save changes" : "Create reason"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
